import type { QueryClient, QueryKey } from "react-query";
import { validate as validateUUID } from "uuid";

import type { CommentThreadSubject } from "@hightouch/lib/llm/asset-comments/types";
import { toSubjectFilter } from "src/components/comments/subject";
import {
  useAmpBoardProjectPinsQuery,
  useAmpBoardProjectQuery,
  useAmpBoardProjectsQuery,
  useAmpBoardGroupsQuery,
  useAmpBoardTagsQuery,
  useAmpBoardTaskQuery,
  useAmpBoardWorkflowsQuery,
  useInboxQuery,
  useAssetCommentThreadsQuery,
} from "src/graphql";
import { queryClient } from "src/lib/react-query";
import { isPendingId } from "src/pages/projects/board-cache";
import { inboxVariables } from "src/pages/projects/inbox/inbox-queries";
import { NO_INBOX_FILTERS } from "src/pages/projects/inbox/inbox-sections";
import {
  ARCHIVED_PROJECTS_VARIABLES,
  OPEN_PROJECTS_VARIABLES,
} from "src/pages/projects/hooks";
import type { LoaderFunction } from "src/router";

type Prefetch = (client: QueryClient) => void;

// A fresh entry is left alone, so a prefetch costs nothing once the page's data is cached.
function warm<T>(client: QueryClient, key: QueryKey, fetch: () => Promise<T>) {
  void client.prefetchQuery(key, fetch);
}

function warmComments(client: QueryClient, subject: CommentThreadSubject) {
  const variables = { subject: toSubjectFilter(subject) };
  warm(
    client,
    useAssetCommentThreadsQuery.getKey(variables),
    useAssetCommentThreadsQuery.fetcher(variables),
  );
}

/** The lookups every page of the area reads, and the open projects and pins the nav lists from. */
function prefetchArea(client: QueryClient) {
  warm(
    client,
    useAmpBoardWorkflowsQuery.getKey(),
    useAmpBoardWorkflowsQuery.fetcher(),
  );
  const workspaceId = window.Hightouch?.workspaceId;
  if (workspaceId) {
    // The same variables as useGroups, so the warmed entry is the one it reads.
    const groupsVariables = { workspaceId: String(workspaceId) };
    warm(
      client,
      useAmpBoardGroupsQuery.getKey(groupsVariables),
      useAmpBoardGroupsQuery.fetcher(groupsVariables),
    );
  }
  warm(client, useAmpBoardTagsQuery.getKey(), useAmpBoardTagsQuery.fetcher());
  warm(
    client,
    useAmpBoardProjectsQuery.getKey(OPEN_PROJECTS_VARIABLES),
    useAmpBoardProjectsQuery.fetcher(OPEN_PROJECTS_VARIABLES),
  );
  warm(
    client,
    useAmpBoardProjectPinsQuery.getKey(),
    useAmpBoardProjectPinsQuery.fetcher(),
  );
}

/** Everything a task's page loads: the task with its subtasks and events, and its comments. */
export function prefetchTaskPage(client: QueryClient, taskId: string) {
  // A task still being created has no page yet.
  if (isPendingId(taskId)) return;
  warm(
    client,
    useAmpBoardTaskQuery.getKey({ id: taskId }),
    useAmpBoardTaskQuery.fetcher({ id: taskId }),
  );
  warmComments(client, { kind: "ampTask", ampTaskId: taskId });
}

/** A project's page: the project and its comments; its task views load their own pages. */
export function prefetchProjectPage(client: QueryClient, projectId: string) {
  warm(
    client,
    useAmpBoardProjectQuery.getKey({ id: projectId }),
    useAmpBoardProjectQuery.fetcher({ id: projectId }),
  );
  warmComments(client, { kind: "ampProject", ampProjectId: projectId });
}

// Requests take the workspace and its region from `window.Hightouch`, which the user context sets as it first renders.
const waiting: Prefetch[] = [];

function whenWorkspaceIsKnown(prefetch: Prefetch) {
  if (window.Hightouch?.workspaceId) prefetch(queryClient);
  else waiting.push(prefetch);
}

/**
 * Runs the prefetches a cold deep link queued before the workspace was known.
 * The area's route calls it on mount, before its pages' chunks and access check load.
 */
export function runWaitingPrefetches() {
  if (!window.Hightouch?.workspaceId) return;
  for (const prefetch of waiting.splice(0)) prefetch(queryClient);
}

/**
 * Route loaders start a page's queries as navigation begins, alongside its
 * chunk, so the page mounts onto a warm cache. They never wait on the data.
 */
const prefetchLoader = (
  prefetch: (params: Record<string, string | undefined>) => Prefetch | null,
) =>
  (({ params }) => {
    const run = prefetch(params);
    if (run) whenWorkspaceIsKnown(run);
    return null;
  }) satisfies LoaderFunction;

export const areaLoader = prefetchLoader(() => prefetchArea);

/** The unfiltered inbox, as it first opens. */
function prefetchInboxQuery(client: QueryClient) {
  const variables = inboxVariables(NO_INBOX_FILTERS);
  warm(
    client,
    useInboxQuery.getKey(variables),
    useInboxQuery.fetcher(variables),
  );
}

/** Everything the inbox opened from the nav reads, so it shows without a load. Entries still fresh are left alone. */
export function prefetchInbox() {
  whenWorkspaceIsKnown((client) => {
    prefetchArea(client);
    prefetchInboxQuery(client);
  });
}

// A malformed id has nothing to fetch; its page shows it isn't found.
export const taskLoader = prefetchLoader(({ taskId }) =>
  taskId && validateUUID(taskId)
    ? (client) => prefetchTaskPage(client, taskId)
    : null,
);

// The archive is small, so switching to it shows at once.
export const projectsListLoader = prefetchLoader(
  () => (client) =>
    warm(
      client,
      useAmpBoardProjectsQuery.getKey(ARCHIVED_PROJECTS_VARIABLES),
      useAmpBoardProjectsQuery.fetcher(ARCHIVED_PROJECTS_VARIABLES),
    ),
);

export const projectLoader = prefetchLoader(({ projectId }) =>
  projectId && validateUUID(projectId)
    ? (client) => prefetchProjectPage(client, projectId)
    : null,
);
